import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useSelector } from 'react-redux';
import ConfirmModal from 'Components/ConfirmModal';
import UIButton from 'Components/UIComponents/UIButton';
import arrayMutators from 'final-form-arrays';
import { useModal } from 'Hooks/Common';
import { useDocumentFieldDelete } from 'Hooks/DocumentFields';
import { useTeamMembersGet } from 'Hooks/Team';
import { OrderingDirection } from 'Interfaces/Common';
import { Document, DocumentTypes, DocumentValues, SignAction } from 'Interfaces/Document';
import { TeamMember } from 'Interfaces/Team';
import { User } from 'Interfaces/User';
import { isEmpty } from 'lodash-es';
import { selectTeamMembers, selectUser } from 'Utils/selectors';

import SelectActionFormFields from './SelectActionFormFields';

interface SelectActionProps {
  initialValues?: DocumentValues;
  currentDocument?: Document;
  setCurrentDocumentType: (signType: DocumentTypes) => void;
  onSubmit: (values: DocumentValues) => void;
  isLoading?: boolean;
  isChooseActionDisabled?: boolean;
  showFormTitle?: boolean;
}

function getSignType(action: SignAction) {
  switch (action) {
    case SignAction.SignDocument:
      return DocumentTypes.ME;
    case SignAction.SignAndSend:
      return DocumentTypes.ME_AND_OTHER;
    case SignAction.Send:
      return DocumentTypes.OTHERS;
  }
}

function getActionType(type: DocumentTypes) {
  switch (type) {
    case DocumentTypes.ME:
      return SignAction.SignDocument;
    case DocumentTypes.ME_AND_OTHER:
      return SignAction.SignAndSend;
    case DocumentTypes.OTHERS:
      return SignAction.Send;
  }
}

const SelectActionForm = ({
  initialValues,
  onSubmit,
  currentDocument,
  setCurrentDocumentType,
  isLoading,
  isChooseActionDisabled,
  showFormTitle = true,
}: SelectActionProps) => {
  const deleteDocumentField = useDocumentFieldDelete();
  const [getTeamMembers] = useTeamMembersGet();
  const [selectedAction, setSelectedAction] = useState<SignAction>(
    (currentDocument && getActionType(currentDocument.type)) ||
      (initialValues && getActionType(initialValues.type)) ||
      SignAction.SignDocument,
  );
  const { teamId }: User = useSelector(selectUser);
  const teamMembers: TeamMember[] = useSelector(selectTeamMembers);
  const valuesRef = useRef<DocumentValues>(initialValues as DocumentValues);
  const signType = getSignType(selectedAction);

  const handleActionSelected = useCallback(
    (action: SignAction) => {
      setCurrentDocumentType(getSignType(action));
      setSelectedAction(action);
    },
    [setCurrentDocumentType],
  );

  const [openConfirmationModal, closeConfirmationModal] = useModal(() => {
    const handleCloseConfirmationModal = () => {
      currentDocument?.fields.forEach(field => {
        deleteDocumentField(field);
      });

      onSubmit({
        ...valuesRef.current,
        type: signType,
        signers: [currentDocument?.signers.find(signer => signer.isPreparer)],
        recipients: valuesRef.current.recipients,
      });
      closeConfirmationModal();
    };

    return (
      <ConfirmModal
        onConfirm={handleCloseConfirmationModal}
        onCancel={closeConfirmationModal}
        onClose={closeConfirmationModal}
        hideCloseIcon
        shouldCloseOnOverlayClick={false}
      >
        <div className="modal__header">
          <h4 className="modal__title">Document Modified</h4>
        </div>
        <p className="modal__subTitle modal__subTitle--center">
          All your changes will be lost. Are you sure you want to continue?
        </p>
      </ConfirmModal>
    );
  }, [currentDocument, signType, onSubmit, deleteDocumentField]);

  const handleSubmit = (values: DocumentValues) => {
    valuesRef.current = values;

    const hasOtherSigners =
      signType === DocumentTypes.ME &&
      currentDocument?.signers.find(signer => !signer.isPreparer);

    if (hasOtherSigners) {
      openConfirmationModal();
    } else {
      onSubmit({ ...values, type: signType });
    }
  };

  const handleGetTeamMembers = useCallback(async () => {
    await getTeamMembers({
      orderingKey: 'role',
      orderingDirection: OrderingDirection.DESC.toUpperCase(),
    });
  }, [getTeamMembers]);

  useEffect(() => {
    if (teamId && isEmpty(teamMembers)) {
      handleGetTeamMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Form
      initialValues={
        selectedAction === SignAction.SignDocument
          ? {
              ...(initialValues as NonNullable<DocumentValues>),
              signers: initialValues?.signers,
            }
          : initialValues
      }
      onSubmit={handleSubmit}
      mutators={{ ...arrayMutators }}
      render={(renderProps: FormRenderProps<DocumentValues>) => (
        <SelectActionFormFields
          {...renderProps}
          isChooseActionDisabled={isChooseActionDisabled}
          selectedAction={selectedAction}
          onActionSelected={handleActionSelected}
          showFormTitle={showFormTitle}
        >
          <UIButton
            priority="primary"
            title="Continue"
            type="submit"
            isLoading={isLoading}
            disabled={
              selectedAction === SignAction.SignDocument
                ? isLoading
                : (renderProps.values.signers && renderProps.values.signers.length < 2) ||
                  renderProps.hasValidationErrors ||
                  renderProps.submitting ||
                  isLoading
            }
          />
        </SelectActionFormFields>
      )}
    />
  );
};

export default SelectActionForm;
